import React, { ReactNode, useEffect, useState } from 'react';
import { RoutesConfig } from '../../config/routes';
import useCurrentRoutePath from '../../hooks/use-current-route-path';
import { isCustomPageType, isDefaultPageType } from '../../hooks/use-custom-routes';
import { useCustomPagePathAsCssClass } from '../../hooks/use-set-class';
import { PassportPageType } from '../../lib/api/custom-page.types';
import Header, { IHeaderLogoData } from './Header';
import Footer from './Footer';
import { useCustomSettings } from '../../hooks/use-custom-settings';
import useSetKeyIdClass, { getCustomPageIdKeyByType } from '../../hooks/use-set-key-id-class';
import useSetId from '../../hooks/use-set-id';
import useLabelFromPath from '../../hooks/use-label-from-path';
import { AppConfigurator } from '../../lib/services/app-configuration/AppConfigurator';

type LayoutItem = 'header' | 'main' | 'footer';
type LayoutItemsProps = Record<LayoutItem, { className?: string, children?: ReactNode | null }>;
type Props = {
  routeLayoutConfig?: Record<string, LayoutItemsProps>,
  customRouteLayoutConfig?: Record<string, LayoutItemsProps>,
  routesConfig: RoutesConfig,
} & { children?: ReactNode } & Partial<LayoutItemsProps>;

const Layout: React.FunctionComponent<Props> = (props: Props): JSX.Element => {
  const [headerLogoData, setHeaderLogoData] = useState<IHeaderLogoData>({
    showWebsiteLogoImage: true,
    websiteLogoUrl: '',
    websiteLogoTitle: '',
    shouldRenderLogo: true,
  });
  const isMeetLink = AppConfigurator.isMeetLinksProfile;
  const isFileDownload = AppConfigurator.isFileDownloadProfile;
  const pathKey = useCurrentRoutePath();
  const {
    primaryColor,
    customCss,
    showWebsiteLogoImage,
    websiteLogoTitle,
    websiteLogoUrl,
    pageTitle
  } = useCustomSettings();
  const key = useSetKeyIdClass();

  useEffect(() => {
    const css = customCss || '';
    const elem = document.querySelector('[data-custom-css="customCss"]');
    if (elem) {
      elem.innerHTML = css;
    }
  }, [customCss]);

  useEffect(() => {
    setHeaderLogoData({
      showWebsiteLogoImage: isMeetLink || isFileDownload || !!showWebsiteLogoImage,
      websiteLogoUrl: websiteLogoUrl,
      websiteLogoTitle: websiteLogoTitle,
      shouldRenderLogo: isMeetLink || isFileDownload || !!showWebsiteLogoImage || !!websiteLogoUrl,
    });
  }, [websiteLogoUrl, showWebsiteLogoImage, websiteLogoTitle, isMeetLink, isFileDownload]);

  const currentPath = window.location.pathname;
  const routeType = props.routesConfig[currentPath]?.type;
  const routeLabel = useLabelFromPath(currentPath, props.routesConfig);
  useCustomPagePathAsCssClass(currentPath, routeType as PassportPageType);

  const idKey = routeType
    ? (isDefaultPageType(routeType) && String(routeType).toLowerCase()) || (isCustomPageType(routeType) && getCustomPageIdKeyByType(routeType)) || ''
    : key
  ;
  useSetId(idKey);

  useEffect(() => {
    document.title = `${routeLabel ? routeLabel + ' |' : ''} ${pageTitle ? pageTitle : ''}`;
  }, [routeLabel, pageTitle]);

  /* Layout config for base layout components - header, main content and footer. Can be set per Route path. */
  const headerProps = props.customRouteLayoutConfig?.[routeType as string]?.header ?? props.routeLayoutConfig?.[pathKey as string]?.header ?? props.header;
  const footerProps = props.customRouteLayoutConfig?.[routeType as string]?.footer ?? props.routeLayoutConfig?.[pathKey as string]?.footer ?? props.footer;
  const mainProps = props.customRouteLayoutConfig?.[routeType as string]?.main ?? props.routeLayoutConfig?.[pathKey as string]?.main ?? props.main;
  const customStyle = { '--primary-color': primaryColor } as React.CSSProperties;

  return (
    <>
      <Header {...headerProps} headerLogoData={headerLogoData} customStyle={customStyle} />
      <main {...mainProps} style={customStyle}>
        {props.children}
        {mainProps?.children ?? null}
      </main>
      <Footer {...footerProps} style={customStyle} />
    </>
  );
};

export default Layout;

